import { addResponseStatusToSpan } from '../../../utils/httpTracing';
import type { DateTime, Schema } from 'effect';
import { Effect, Request, RequestResolver } from 'effect';

import type {
  ApiInfrastructureErrors,
  BackendApiErrorUnion,
} from '../../../data/error/backendApi/_internal/createBackendApiError';
import { createBackendApiError } from '../../../data/error/backendApi/_internal/createBackendApiError';
import type { SearchSuggestAdapters } from '../../../module/search/searchAdapters';
import { liftRequest } from '../context';
import { BackendApi } from '../../../service/api/BackendApi';
import type { AuthToken } from '../../../service/auth/AuthToken';
import type { LoggerConfig } from '../../../service/logger/LoggerConfig';
import { ApiModule } from '../../../service/logger/ApiModule';
import { logApiOperation } from '../../../utils/logger/apiLogger';
import { schemaBodyJsonClean } from '@vs-shop/schemas/transform/schemaBodyJsonClean';
import type { StripJsonLd } from '@vs-shop/schemas/transform/withoutJsonLd';
import type { HttpClient } from 'effect/unstable/http/HttpClient';
import { matchStatus } from 'effect/unstable/http/HttpClientResponse';

const ENDPOINT = '/api/v2/shop/search/suggest';
const HTTP_METHOD = 'get';

type GetSearchSuggest = {
  q: string;
};

export type MakeGetSearchSuggestDeps<SuggestResult extends Schema.Top> = {
  readonly suggestResult: SuggestResult;
  readonly searchSuggestAdapters: SearchSuggestAdapters<StripJsonLd<SuggestResult['Type']>>;
};

export const makeGetSearchSuggest = <SuggestResult extends Schema.Top>(
  deps: MakeGetSearchSuggestDeps<SuggestResult>
) => {
  const GetSearchSuggestError = createBackendApiError({
    domain: 'Search',
    endpoint: 'GetSearchSuggest',
    path: ENDPOINT,
    identifierField: 'q',
  });

  type GetSearchSuggestRequestError = BackendApiErrorUnion<typeof GetSearchSuggestError>;

  const getSearchSuggestFetch = ({ q }: GetSearchSuggest) =>
    Effect.gen(function* () {
      const apiClient = yield* BackendApi;

      return yield* apiClient
        .path(ENDPOINT)
        .method(HTTP_METHOD)
        .accept('application/ld+json')
        .fetch({
          query: {
            q,
          },
        });
    }).pipe(
      Effect.withSpan(`http.${HTTP_METHOD} ${ENDPOINT}`, {
        kind: 'client',
        attributes: {
          'http.request.method': HTTP_METHOD,
          'search.query': q,
        },
      })
    );

  const getSearchSuggest = (props: GetSearchSuggest) =>
    getSearchSuggestFetch(props).pipe(
      Effect.map((response) => response.response),
      Effect.tap(addResponseStatusToSpan),
      Effect.withSpan('ValidateResponse'),
      Effect.flatMap(
        matchStatus({
          '2xx': (response) => schemaBodyJsonClean(deps.suggestResult)(response),
          404: (response) =>
            Effect.fail(new GetSearchSuggestError.NotFoundError({ q: props.q, response })),
          orElse: (response) =>
            Effect.fail(new GetSearchSuggestError.ApiRequestError({ q: props.q, response })),
        })
      ),
      Effect.tap((result) => {
        const counts = deps.searchSuggestAdapters.counts(result);
        return logApiOperation(
          ApiModule.SearchSuggest,
          'Debug',
          `Resolved suggest for "${props.q}": ${String(counts.products)} products, ${String(counts.brands)} brands, ${String(counts.taxons)} taxons, ${String(counts.articles)} articles`
        );
      }),
      Effect.withSpan('api.search.suggest', {
        kind: 'internal',
        attributes: {
          'search.query': props.q,
        },
      })
    );

  type GetSearchSuggestRequest = {
    readonly _tag: 'GetSearchSuggestRequest';
    readonly q: string;
  } & Request.Request<
    StripJsonLd<SuggestResult['Type']>,
    GetSearchSuggestRequestError | ApiInfrastructureErrors,
    BackendApi | AuthToken | HttpClient | LoggerConfig | DateTime.CurrentTimeZone
  >;

  const GetSearchSuggestRequest =
    Request.tagged<GetSearchSuggestRequest>('GetSearchSuggestRequest');

  const GetSearchSuggestResolver = RequestResolver.fromEffect(
    liftRequest((request: GetSearchSuggestRequest) => getSearchSuggest({ q: request.q }))
  );

  const requestSearchSuggest = (props: GetSearchSuggest) =>
    Effect.request(GetSearchSuggestRequest(props), GetSearchSuggestResolver);

  return { getSearchSuggest, requestSearchSuggest, GetSearchSuggestError };
};
